import { Contract, formatUnits, type JsonRpcProvider } from 'ethers';
import { Connection, PublicKey } from '@solana/web3.js';
import type { Position } from '@/api/feeDashboard';

// Reads fee balances straight from chain, client-side.
//
// The dashboard API deliberately returns no balances: they are live and
// per-token, cheap for one browser to fetch from its own RPC but expensive and
// rate-limited to fan out server-side. Each position instead carries a `read`
// block with everything needed to make the call, which is what this module
// consumes.

const FEE_MANAGER_ABI = ['function getRelayerFee(address relayer, address token) external view returns (uint256)'];
const ERC20_ABI = [
	'function decimals() external view returns (uint8)',
	'function symbol() external view returns (string)',
];

export type FeeBalance = {
	raw: bigint;
	decimals: number;
	display: string;
	symbol?: string;
	// True when the balance clears the relayer's minimum for this token. Null
	// when the token is not auto-redeemed at all, so there is no minimum.
	aboveMinimum: boolean | null;
};

export type BalanceState = { status: 'loading' } | { status: 'ok'; value: FeeBalance } | { status: 'error'; message: string };

// (chainId, token) -> metadata. Token metadata never changes, so one lookup per
// token per session is enough.
const metaCache = new Map<string, { decimals: number; symbol?: string }>();

function metaKey(chainId: number, token: string): string {
	return `${chainId}:${token.toLowerCase()}`;
}

export function positionKey(p: Position): string {
	return `${p.chainId}:${p.owner}:${p.token}`;
}

async function evmTokenMeta(
	provider: JsonRpcProvider,
	chainId: number,
	token: string,
	fallbackDecimals: number | null,
): Promise<{ decimals: number; symbol?: string }> {
	const key = metaKey(chainId, token);
	const cached = metaCache.get(key);
	if (cached) {
		return cached;
	}
	// The zero address is the native-asset sentinel; it has no ERC-20 to call.
	if (/^0x0{40}$/i.test(token)) {
		const meta = { decimals: 18, symbol: 'ETH' };
		metaCache.set(key, meta);
		return meta;
	}
	try {
		const erc20 = new Contract(token, ERC20_ABI, provider);
		const [decimals, symbol] = await Promise.all([erc20.decimals(), erc20.symbol().catch(() => undefined)]);
		const meta = { decimals: Number(decimals), symbol };
		metaCache.set(key, meta);
		return meta;
	} catch {
		// A non-standard token should degrade to a raw display, not break the row.
		return { decimals: fallbackDecimals ?? 18 };
	}
}

export async function readEvmBalance(
	provider: JsonRpcProvider,
	position: Position,
): Promise<FeeBalance> {
	if (position.read.kind !== 'evm') {
		throw new Error('not an evm position');
	}
	const { feeManager, owner, token } = position.read;
	if (!feeManager) {
		throw new Error('fee manager address not configured');
	}
	const contract = new Contract(feeManager, FEE_MANAGER_ABI, provider);
	// `token` here is the argument the API resolved for us, NOT the recorded
	// token: native fees are keyed under WETH and only readable via
	// address(0), so passing the recorded address would read an empty slot.
	const raw: bigint = await contract.getRelayerFee(owner, token);
	const meta = await evmTokenMeta(provider, position.chainId, position.token, position.minAmount?.decimals ?? null);
	return toBalance(raw, meta.decimals, meta.symbol, position);
}

export async function readSolanaBalance(connection: Connection, position: Position): Promise<FeeBalance> {
	if (position.read.kind !== 'solana') {
		throw new Error('not a solana position');
	}
	const { mixedFeeAta } = position.read;
	if (!mixedFeeAta) {
		throw new Error('fee account address could not be derived');
	}
	try {
		const res = await connection.getTokenAccountBalance(new PublicKey(mixedFeeAta));
		return toBalance(BigInt(res.value.amount), res.value.decimals, undefined, position);
	} catch (err) {
		// A fee account that has never received anything simply does not exist
		// on chain yet — that is a zero balance, not a failure.
		if (String((err as Error).message).includes('could not find account')) {
			return toBalance(0n, position.minAmount?.decimals ?? 6, undefined, position);
		}
		throw err;
	}
}

function toBalance(raw: bigint, decimals: number, symbol: string | undefined, position: Position): FeeBalance {
	return {
		raw,
		decimals,
		display: formatUnits(raw, decimals),
		symbol,
		// The API hands over the exact threshold the redeem cron applies, so
		// "below minimum" is decided here against the balance we just read.
		aboveMinimum: position.minAmount ? raw >= BigInt(position.minAmount.raw) : null,
	};
}

export function formatAmount(display: string, maximumFractionDigits = 6): string {
	const n = Number(display);
	if (!Number.isFinite(n)) {
		return display;
	}
	if (n > 0 && n < 10 ** -maximumFractionDigits) {
		return `<${10 ** -maximumFractionDigits}`;
	}
	return n.toLocaleString(undefined, { maximumFractionDigits });
}

// Raw base units -> human string. `decimals` comes from the API per row; when
// it is null the token is unknown, and showing the raw integer is the only
// honest option — scaling by a guessed 6 turned 21 USDT into 21 trillion.
export function formatRaw(raw: string | null, decimals: number | null): string {
	if (!raw) {
		return '—';
	}
	if (decimals == null) {
		return raw;
	}
	try {
		return formatAmount(formatUnits(BigInt(raw), decimals));
	} catch {
		return raw;
	}
}
